import { localMonthKey, monthsBetween, monthTitle, shiftMonthKey, type StaffClassesReport, type StaffClassesRow, type StaffClassesSession } from "@teamna/shared";
import { useEffect, useState } from "react";
import {
  Badge,
  Button,
  DataTable,
  EmptyState,
  ErrorState,
  InlineError,
  Notice,
  SectionHeader,
  Select,
  SkeletonList,
  StatTile,
  StepArrows,
  type BadgeTone,
  type DataTableColumn,
} from "../components/index.ts";
import { track } from "@teamna/web-client/analytics/web.ts";
import { useLabels } from "@teamna/web-client/hooks/use-labels.ts";
import { useExportStaffClasses, useStaffClasses } from "@teamna/web-client/hooks/use-reports.ts";
import { formatDayShort, formatTime } from "@teamna/web-client/lib/format.ts";
import { downloadText } from "./download.ts";
import { OfficePage, runsBusiness } from "./office.tsx";

/**
 * Classes per staff member (Pilates studio shape, T2), under Team in the owner menu. A business that
 * pays its staff per class reads one month here: for each person, the classes and the privates they
 * ran and the people present, then each session, and a CSV file with one row per session.
 *
 * The month is the business's own calendar month. The counts come from the API
 * (`packages/core/src/services/staff-classes.ts`), which has the rules: a cover counts for the person
 * who covered, a cancelled session does not count, a booking with no mark is not counted as present.
 * Owner and admin only: it is pay data. Not on mobile: a monthly report is an office job (decision D1).
 */

export type StaffClassesWords = {
  /** "coach", "instructor": the business's own word. */
  staff: string;
  staffPlural: string;
  /** "swimmers", "clients". */
  participants: string;
  /** "level", "class". */
  group: string;
};

const ATTENDANCE: Record<StaffClassesSession["attendance"], { label: string; tone: BadgeTone }> = {
  taken: { label: "Taken", tone: "confirmed" },
  not_taken: { label: "Not taken", tone: "late" },
  nobody: { label: "Nobody booked", tone: "quiet" },
};

function capital(word: string): string {
  return word ? word[0]!.toUpperCase() + word.slice(1) : word;
}

/** "1 session", "3 sessions". */
export function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

/** The value of the "every person" choice of the session filter. */
const EVERYONE = "everyone";
/** The value of the row of sessions with nobody set (its id is null). */
const NOBODY = "nobody";

const keyOf = (staffUserId: string | null) => staffUserId ?? NOBODY;

export function OwnerStaffClassesScreen() {
  const { label } = useLabels();
  return (
    <OfficePage
      title={`Classes per ${label("staff")}`}
      description={`What each ${label("staff")} ran in a month, and who came. Use it to pay your team.`}
    >
      {(business) =>
        runsBusiness(business) ? (
          <StaffClassesPage businessId={business.businessId} timezone={business.timezone} />
        ) : (
          <EmptyState title="Owner and admin only" message="Ask the owner for this report." />
        )
      }
    </OfficePage>
  );
}

function StaffClassesPage({ businessId, timezone }: { businessId: string; timezone: string }) {
  const { label } = useLabels();
  const words: StaffClassesWords = {
    staff: label("staff"),
    staffPlural: label("staff", { plural: true }),
    participants: label("participant", { plural: true }),
    group: label("group"),
  };
  const thisMonth = localMonthKey(new Date(), timezone);
  const [month, setMonth] = useState(thisMonth);
  const query = useStaffClasses(businessId, month);
  const exporter = useExportStaffClasses(businessId);

  // One view when the report opens on this month. A move to another month sends one more.
  useEffect(() => {
    track("report_viewed", { report: "staff_classes", scope: "team", months_back: 0 });
  }, []);

  function showMonth(next: string) {
    if (next > thisMonth || next === month) return;
    setMonth(next);
    exporter.reset();
    track("report_viewed", { report: "staff_classes", scope: "team", months_back: Math.max(0, monthsBetween(next, thisMonth)) });
  }

  return (
    <StaffClassesView
      month={month}
      thisMonth={thisMonth}
      timezone={timezone}
      words={words}
      report={query.data}
      isLoading={query.isLoading && !query.data}
      error={query.error}
      onRetry={() => void query.refetch()}
      onMonth={showMonth}
      exporting={exporter.isPending}
      exportError={exporter.error}
      onExport={() =>
        exporter.mutate(
          { month },
          { onSuccess: (text) => downloadText(`classes-per-${words.staff.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${month}.csv`, text) },
        )
      }
    />
  );
}

/** The page with its data, for the screen and for Storybook. */
export function StaffClassesView({
  month,
  thisMonth,
  timezone,
  words,
  report,
  isLoading = false,
  error = null,
  onRetry,
  onMonth,
  onExport,
  exporting = false,
  exportError = null,
}: {
  month: string;
  thisMonth: string;
  timezone: string;
  words: StaffClassesWords;
  report: StaffClassesReport | undefined;
  isLoading?: boolean;
  error?: unknown;
  onRetry?: () => void;
  onMonth: (month: string) => void;
  onExport: () => void;
  exporting?: boolean;
  exportError?: unknown;
}) {
  const [shown, setShown] = useState<string>(EVERYONE);
  const sessions = report?.sessions ?? [];
  const hasSessions = sessions.length > 0;

  const heading = (
    <div className="flex flex-wrap items-center gap-3">
      <StepArrows unit="month" onPrevious={() => onMonth(shiftMonthKey(month, -1))} onNext={() => onMonth(shiftMonthKey(month, 1))} nextDisabled={month >= thisMonth} />
      <h2 className="text-[17px] font-semibold">{monthTitle(month)}</h2>
      {month !== thisMonth ? (
        <Button variant="ghost" size="sm" onClick={() => onMonth(thisMonth)}>
          Back to this month
        </Button>
      ) : null}
      <div className="ml-auto">
        <Button variant="secondary" size="sm" disabled={!hasSessions} loading={exporting} onClick={onExport}>
          Export CSV
        </Button>
      </div>
    </div>
  );

  let body;
  if (isLoading) {
    body = <SkeletonList rows={4} />;
  } else if (error && !report) {
    body = <ErrorState error={error} {...(onRetry ? { onRetry } : {})} />;
  } else if (!report || !hasSessions) {
    body = (
      <EmptyState
        title={`No sessions in ${monthTitle(month)}`}
        message={
          month === thisMonth
            ? `No session ran this month yet. A session counts here when it starts.`
            : `No session ran in this month. Cancelled sessions do not count.`
        }
      />
    );
  } else {
    body = <ReportBody report={report} timezone={timezone} words={words} shown={shown} onShown={setShown} thisMonth={month === thisMonth} />;
  }

  return (
    <div className="grid gap-4">
      {heading}
      {exportError ? <InlineError error={exportError} /> : null}
      {body}
    </div>
  );
}

/** The name of a row's person, or "No instructor" for the sessions with nobody set. */
function staffNameOf(row: { staffUserId: string | null; staffName: string | null }, words: StaffClassesWords): string {
  const noStaff = `No ${words.staff}`;
  return row.staffUserId ? (row.staffName ?? noStaff) : noStaff;
}

function staffColumns(words: StaffClassesWords): DataTableColumn<StaffClassesRow>[] {
  return [
    {
      id: "name",
      header: capital(words.staff),
      cell: (row) => <span dir="auto">{staffNameOf(row, words)}</span>,
      sortValue: (row) => (row.staffUserId ? staffNameOf(row, words) : null),
    },
    { id: "classes", header: "Classes", cell: (row) => row.classes, sortValue: (row) => row.classes, align: "end" },
    { id: "privates", header: "Privates", cell: (row) => row.privates, sortValue: (row) => row.privates, align: "end" },
    { id: "present", header: `${capital(words.participants)} present`, cell: (row) => row.present, sortValue: (row) => row.present, align: "end" },
    {
      id: "notTaken",
      header: "Attendance not taken",
      cell: (row) => (row.notTaken > 0 ? <Badge tone="late">{plural(row.notTaken, "session", "sessions")}</Badge> : "None"),
      sortValue: (row) => row.notTaken,
      align: "end",
      hideOnPhone: true,
    },
  ];
}

/**
 * The columns of the session table. `names` null leaves out the staff column: the page of one staff
 * member (`staff-member.tsx`) shows only that person's sessions.
 */
export function sessionColumns(
  timezone: string,
  words: StaffClassesWords,
  names: ReadonlyMap<string, string> | null,
): DataTableColumn<StaffClassesSession>[] {
  const nameOf = (row: StaffClassesSession) => names?.get(keyOf(row.staffUserId)) ?? `No ${words.staff}`;
  const staffColumn: DataTableColumn<StaffClassesSession>[] = names
    ? [{ id: "staff", header: capital(words.staff), cell: (row) => <span dir="auto">{nameOf(row)}</span>, sortValue: nameOf }]
    : [];
  return [
    { id: "day", header: "Day", cell: (row) => formatDayShort(row.startsAt, timezone), sortValue: (row) => row.startsAt },
    { id: "time", header: "Time", cell: (row) => formatTime(row.startsAt, timezone), hideOnPhone: true },
    {
      id: "class",
      header: capital(words.group),
      cell: (row) => (
        <span className="flex flex-wrap items-center gap-1.5">
          <span dir="auto">{row.className}</span>
          {row.private ? <Badge>Private</Badge> : null}
        </span>
      ),
      sortValue: (row) => row.className,
    },
    ...staffColumn,
    { id: "booked", header: "Booked", cell: (row) => row.booked, sortValue: (row) => row.booked, align: "end", hideOnPhone: true },
    { id: "present", header: "Present", cell: (row) => row.present, sortValue: (row) => row.present, align: "end" },
    {
      id: "attendance",
      header: "Attendance",
      cell: (row) => <Badge tone={ATTENDANCE[row.attendance].tone}>{ATTENDANCE[row.attendance].label}</Badge>,
      sortValue: (row) => ATTENDANCE[row.attendance].label,
    },
  ];
}

function ReportBody({
  report,
  timezone,
  words,
  shown,
  onShown,
  thisMonth,
}: {
  report: StaffClassesReport;
  timezone: string;
  words: StaffClassesWords;
  shown: string;
  onShown: (key: string) => void;
  thisMonth: boolean;
}) {
  const names = new Map(report.staff.map((row) => [keyOf(row.staffUserId), staffNameOf(row, words)]));
  // A filter on a person who is not in this month shows everyone.
  const filter = shown !== EVERYONE && names.has(shown) ? shown : EVERYONE;

  const totals = report.staff.reduce(
    (sum, row) => ({ classes: sum.classes + row.classes, privates: sum.privates + row.privates, present: sum.present + row.present, notTaken: sum.notTaken + row.notTaken }),
    { classes: 0, privates: 0, present: 0, notTaken: 0 },
  );

  const visible = filter === EVERYONE ? report.sessions : report.sessions.filter((row) => keyOf(row.staffUserId) === filter);

  return (
    <div className="grid gap-5">
      {thisMonth ? <p className="text-[13px] text-ink-2">This month so far: the sessions that started before now.</p> : null}
      {totals.notTaken > 0 ? (
        <Notice tone="warning" title={`Attendance is not taken for ${plural(totals.notTaken, "session", "sessions")}`}>
          {`A booking with no mark does not count as present. Ask your ${words.staffPlural} to take attendance before you pay.`}
        </Notice>
      ) : null}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StatTile label="Classes" value={totals.classes} />
        <StatTile label="Privates" value={totals.privates} />
        <StatTile label={`${capital(words.participants)} present`} value={totals.present} />
      </div>

      <section className="grid gap-2">
        <SectionHeader title={`Per ${words.staff}`} />
        <DataTable
          columns={staffColumns(words)}
          rows={report.staff}
          rowKey={(row) => keyOf(row.staffUserId)}
          rowLabel={(row) => `the sessions of ${staffNameOf(row, words)}`}
          onRowClick={(row) => onShown(keyOf(row.staffUserId))}
          countLabel={(count) => plural(count, words.staff, words.staffPlural)}
          empty={{ title: "Nobody ran a session", message: "No session ran in this month." }}
        />
      </section>

      <section className="grid gap-2">
        <SectionHeader
          title="Sessions"
          action={
            <Select label={`Show the sessions of`} value={filter} onChange={(event) => onShown(event.target.value)} className="w-full max-w-[220px]">
              <option value={EVERYONE}>{`Every ${words.staff}`}</option>
              {report.staff.map((row) => (
                <option key={keyOf(row.staffUserId)} value={keyOf(row.staffUserId)}>
                  {staffNameOf(row, words)}
                </option>
              ))}
            </Select>
          }
        />
        <DataTable
          columns={sessionColumns(timezone, words, names)}
          rows={visible}
          rowKey={(row) => row.sessionId}
          defaultSort={{ id: "day", direction: "asc" }}
          countLabel={(count) => plural(count, "session", "sessions")}
          empty={{ title: "No sessions", message: `This ${words.staff} ran no session in this month.` }}
        />
      </section>
    </div>
  );
}
